/** An address as the registration forms need it, with anything unknown left undefined. */
export interface ResolvedAddress {
  pincode?: string
  addressLine?: string
  area?: string
  city?: string
  state?: string
}

/**
 * Turns coordinates into an address, through Nominatim.
 *
 * <p>The same OpenStreetMap service the map tiles already come from, so no extra key or account
 * is involved. Shared by every form with a "use my current location" button rather than copied
 * into each: Nominatim names the same thing differently by country and by place, and the list of
 * fallbacks below is the sort of thing that silently rots when it exists in three copies.
 *
 * <p>Throws on a failed lookup. The caller decides what that means - in every current caller it
 * means keeping the pin, which is the half that matters, and asking for the address by hand.
 */
export async function reverseGeocode(lat: number, lng: number): Promise<ResolvedAddress> {
  const res = await fetch(
    `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&addressdetails=1`,
    { headers: { Accept: 'application/json' } },
  )
  if (!res.ok) {
    throw new Error(`reverse geocode failed: ${res.status}`)
  }

  const data = (await res.json()) as { address?: Record<string, string> }
  const a = data.address ?? {}

  const clean = (value?: string) => {
    const trimmed = (value ?? '').trim()
    return trimmed === '' ? undefined : trimmed
  }

  return {
    // Digits only and capped at six: some countries return codes this app's six-digit field
    // cannot hold, and a half-written pincode is worse than an empty one.
    pincode: clean((a.postcode ?? '').replace(/\D/g, '').slice(0, 6)),
    addressLine: clean(
      [a.house_number, a.road ?? a.pedestrian ?? a.neighbourhood].filter(Boolean).join(' '),
    ),
    area: clean(a.suburb ?? a.neighbourhood ?? a.city_district ?? a.village),
    city: clean(a.city ?? a.town ?? a.municipality ?? a.state_district),
    state: clean(a.state),
  }
}

/**
 * A pincode's centre point, through the same service.
 *
 * <p>The map centres on the caller's stored coordinates and returns an empty map when there are
 * none - "a map of somewhere else" being the wrong answer. But most people give a pincode and
 * never press "use my current location", so they have a location on file that the map cannot
 * read. This turns the one into the other.
 *
 * <p>Scoped to India by countrycodes, because a six-digit string is a valid postcode in several
 * countries and the wrong one would silently drop the dot on another continent.
 */
export async function geocodePincode(pincode: string): Promise<{ lat: number; lng: number } | null> {
  if (!/^\d{6}$/.test(pincode)) {
    return null
  }
  const res = await fetch(
    `https://nominatim.openstreetmap.org/search?format=jsonv2&countrycodes=in&postalcode=${pincode}&limit=1`,
    { headers: { Accept: 'application/json' } },
  )
  if (!res.ok) {
    throw new Error(`pincode lookup failed: ${res.status}`)
  }
  const rows = (await res.json()) as { lat?: string; lon?: string }[]
  const first = rows?.[0]
  if (!first?.lat || !first?.lon) {
    return null
  }
  const lat = Number(first.lat)
  const lng = Number(first.lon)
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null
}

/**
 * Keeps what the person already typed.
 *
 * <p>Every caller fills only empty fields: someone who has written their address should not have
 * it replaced by whatever the browser thinks their coordinates are.
 */
export function fillIfEmpty(current: string, next?: string): string {
  return current.trim() || (next ?? '')
}
